import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dynVariadic } from "trader.ts/ui";
import { Form, selectBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  formulaid,
  instrumentid,
  internalteacherid,
  studentid,
  waitinglistformulaid,
  waitinglistid,
  waitinglisttimerangeid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import { renderDeleteButton } from "../../client/savebutton";
import { debounce } from "../../lib/debounce";
import { isEqual } from "../../lib/isequal";
import { allWeekdays } from "../../lib/time";
import { ddMMyyyy, renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";
import { translateWeekday } from "../../translations_specific";
import { renderTableHeaderCellWithSorting } from "../payments_overview_non_periodic/client";

type item = {
  id: waitinglistid;
  createdon: joda.Instant;
  student: {
    id: studentid;
    firstname: string;
    lastname: string;
  };
  instrument: {
    id: instrumentid;
    description: string;
  };
  teacher: {
    internalteacherid: internalteacherid | null;
    firstname: string | null;
    lastname: string | null;
  };
  ranges: {
    id: waitinglisttimerangeid;
    weekday: joda.DayOfWeek;
    starttime: joda.LocalTime;
    endtime: joda.LocalTime;
  }[];
  formulas:
    | {
        id: waitinglistformulaid;
        formulaid: formulaid;
        description: string;
      }[]
    | null;
};

type possibleSorting = "student" | "instrument" | "createdon";

export function render(opts: { items: item[] }): HTMLElement | HTMLElement[] {
  const itemsS = new Source(opts.items); // needs to be source so we can delete
  const itemsFilteredS: Source<item[]> = new Source(itemsS.get());

  const allRelevantInstruments = opts.items
    .map((i) => i.instrument)
    .uniqBy((i) => i.id)
    .orderBy((i) => i.description);

  const allRelevantFormulas = opts.items
    .flatMap((i) => i.formulas || [])
    .uniqBy((f) => f.formulaid)
    .orderBy((f) => f.description);

  const form = new Form({})
    .addField("instruments", [], async () => {
      const values = [
        {
          label: "Allemaal",
          values: allRelevantInstruments,
        },
        ...allRelevantInstruments.map((t) => ({
          label: t.description,
          values: [t],
        })),
      ];
      return selectBox({
        values: values,
        initial: values[0],
        show: (t) => t.label,
        label: "Cursussen",
      });
    })
    .addField("formulas", [], async () => {
      const values = [
        {
          label: "Allemaal",
          values: allRelevantFormulas,
        },
        ...allRelevantFormulas.map((t) => ({
          label: t.description,
          values: [t],
        })),
      ];
      return selectBox({
        values: values,
        initial: values[0],
        show: (t) => t.label,
        label: "Formules",
      });
    })
    .addField("weekdays", [], async () => {
      const values = [
        {
          label: "Allemaal",
          values: allWeekdays,
        },
        ...allWeekdays.map((t) => ({
          label: translateWeekday(t),
          values: [t],
        })),
      ];
      return selectBox({
        values,
        initial: values[0],
        show: (i) => i.label,
        label: "Weekdagen",
      });
    })
    .build();

  function runFilters() {
    const formValues = form.s.get();
    if (formValues.tag === "parsed") {
      const filters = formValues.parsed;
      itemsFilteredS.set(
        itemsS.get().filter((i) => {
          const instrumentsMatch = filters.instruments.values.some(
            (instr) => instr.id === i.instrument.id,
          );
          const formulasMatch =
            (i.formulas || []).length === 0 ||
            filters.formulas.values.some((f) =>
              (i.formulas || []).some((f2) => f.formulaid === f2.formulaid),
            );
          const weekdaysMatch =
            i.ranges.length === 0 ||
            filters.weekdays.values.some((wd) =>
              i.ranges.some((range) => isEqual(range.weekday, wd)),
            );
          return instrumentsMatch && formulasMatch && weekdaysMatch;
        }),
      );
    }
  }

  const runFiltersDebounced = debounce(runFilters, 250);

  // I don't expect this list to get too long, so I'll just do filtering on clientside
  // Can probably do some other screens like that as well...
  form.s.observe(runFiltersDebounced);

  const tableid = "classy-table-id";

  const downloadLinks = renderTableDownloadLinks({
    tableid,
    filename: "Wachtlijst",
  });

  const sortingS: Source<{
    sorting: possibleSorting;
    direction: "asc" | "desc";
  }> = new Source({ sorting: "student", direction: "asc" });

  function handleDelete(id: waitinglistid) {
    itemsFilteredS.set(itemsFilteredS.get().filter((i) => i.id !== id));
    itemsS.set(itemsS.get().filter((i) => i.id !== id));
  }

  return (
    <main style="max-width: unset;">
      <section className={style.equal_columns + style.cancel_main_margins}>
        {form.render()}
      </section>
      {downloadLinks}
      <table id={tableid}>
        {dynVariadic([itemsFilteredS, sortingS], ([itemsFiltered, sorting]) => {
          return [
            renderHeaderRow(sorting, sortingS),
            ...itemsFiltered
              .orderBy((i) => {
                return sorting.sorting === "student"
                  ? renderFirstNameLastName(i.student)
                  : sorting.sorting === "instrument"
                  ? i.instrument.description
                  : sorting.sorting === "createdon"
                  ? i.createdon.toEpochMilli()
                  : checkAllCasesHandled(sorting.sorting);
              }, sorting.direction)
              .map((i) => renderRow(i, handleDelete)),
          ];
        })}
      </table>
    </main>
  );
}

function renderHeaderRow(
  currentSorting: {
    sorting: possibleSorting;
    direction: "asc" | "desc";
  },
  sortingS: Source<{
    sorting: possibleSorting;
    direction: "asc" | "desc";
  }>,
) {
  function renderHeaderCell(label: string, sorting: possibleSorting | null) {
    return renderTableHeaderCellWithSorting(
      label,
      sorting,
      currentSorting,
      sortingS,
    );
  }
  return (
    <tr style="position: sticky; top: 0px; background-color: white;">
      {renderHeaderCell("Student", "student")}
      {renderHeaderCell("Cursus", "instrument")}
      {
        // renderHeaderCell("Docent")
      }
      {renderHeaderCell("Formules", null)}
      {renderHeaderCell("Aangemaakt op", "createdon")}
      <th></th>
    </tr>
  );
}

function renderRow(item: item, handleDelete: (id: waitinglistid) => void) {
  return (
    <tr>
      <td>
        <a
          href={routes.studentOverviewRoute.link({
            studentid: item.student.id,
          })}
        >
          {renderFirstNameLastName(item.student)}
        </a>
      </td>
      <td>{item.instrument.description}</td>
      {
        // <td>
        //   {item.teacher.firstname && item.teacher.lastname
        //     ? renderFirstNameLastName({
        //         firstname: item.teacher.firstname,
        //         lastname: item.teacher.lastname,
        //       })
        //     : ""}
        // </td>
      }
      <td>
        {(item.formulas || []).map((f) => (
          <div>{f.description}</div>
        ))}
      </td>
      <td>
        {item.createdon.atZone(joda.ZoneId.UTC).toLocalDate().format(ddMMyyyy)}
      </td>
      <td>
        {renderDeleteButton({
          label: <i className={FA.icon_trash_alt_solid}></i>,
          className: style.button_has_only_icon,
          confirmationLabel: "Ben je zeker dat je dit van de wachtlijst wilt verwijderen?",
          dontShowLabelAgainInConfirmation: true,
          doDelete: async () => {
            await rpc(routes.deleteFromWaitinglistSpec, undefined, {
              id: item.id,
            });
            handleDelete(item.id);
            return null;
          },
          redirectTo: "",
          panelStyle: `
          position: absolute;
          top: 48px;
          right: 0px;
          z-index: 1000;
          box-shadow: var(--box-shadow-large);`,
        })}
      </td>
      {
        // <td>
        //   {(item.ranges || []).map((r) => (
        //     <div>
        //       {translateWeekday(r.weekday) +
        //         " - " +
        //         r.starttime.format(HHmm) +
        //         " - " +
        //         r.endtime.format(HHmm)}
        //     </div>
        //   ))}
        // </td>
      }
    </tr>
  );
}
